import React, { useEffect, useRef, useState } from 'react';

const AnimatedAvatar = ({ isSpeaking = false, isListening = false, isThinking = false }) => {
    const [blink, setBlink] = useState(false);
    const [mouthOpen, setMouthOpen] = useState(false);
    const [headY, setHeadY] = useState(0);
    const blinkRef = useRef(null);
    const mouthRef = useRef(null);
    const headRef = useRef(null);

    useEffect(() => {
        const next = () => {
            const delay = 2800 + Math.random() * 3200;
            blinkRef.current = setTimeout(() => {
                setBlink(true);
                setTimeout(() => setBlink(false), 140);
                next();
            }, delay);
        };
        next();
        return () => clearTimeout(blinkRef.current);
    }, []);

    useEffect(() => {
        clearInterval(mouthRef.current);
        if (!isSpeaking) { setMouthOpen(false); return; }
        mouthRef.current = setInterval(() => setMouthOpen(v => !v), 170);
        return () => clearInterval(mouthRef.current);
    }, [isSpeaking]);

    useEffect(() => {
        let t = 0;
        const tick = () => {
            t += 0.02;
            setHeadY(Math.sin(t) * 2.2);
            headRef.current = requestAnimationFrame(tick);
        };
        headRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(headRef.current);
    }, []);

    return (
        <div className="relative flex items-center justify-center select-none" style={{ width: 176, height: 176 }}>
            {(isSpeaking || isListening) && (
                <>
                    <div className="absolute rounded-full animate-ping" style={{
                        inset: -6, background: 'rgba(45,122,79,0.10)', animationDuration: '1.8s'
                    }} />
                    <div className="absolute rounded-full animate-ping" style={{
                        inset: -16, background: 'rgba(45,122,79,0.05)', animationDuration: '2.4s', animationDelay: '0.5s'
                    }} />
                </>
            )}

            {/* Anillo exterior gris suave */}
            <div className="absolute inset-0 rounded-full" style={{ background: '#DCE3E5' }} />

            <svg viewBox="0 0 180 180" width="176" height="176" style={{ position: 'relative', zIndex: 1 }} xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <clipPath id="av-inner"><circle cx="90" cy="90" r="78" /></clipPath>
                </defs>

                {/* Círculo coral interior */}
                <circle cx="90" cy="90" r="78" fill="#E87A5D" />

                <g clipPath="url(#av-inner)">

                    {/* Cuerpo / saco */}
                    <path d="M 6 180 C 6 152 42 138 90 136 C 138 138 174 152 174 180 Z" fill="#1F2A3A" />
                    {/* Camisa blanca con V */}
                    <path d="M 70 142 L 90 174 L 110 142 L 110 180 L 70 180 Z" fill="#F5F3ED" />
                    {/* Cuello (piel) */}
                    <path d="M 76 124 Q 90 134 104 124 L 106 142 Q 90 150 74 142 Z" fill="#EBC0A0" />

                    {/* Cabeza — escalada para ocupar más espacio */}
                    <g transform="translate(90 90) scale(1.28) translate(-90 -86)">
                    <g style={{ transform: `translateY(${headY}px)`, transformOrigin: '90px 80px' }}>

                        {/* Orejas (detrás de la cara) */}
                        <ellipse cx="58" cy="88" rx="4" ry="7" fill="#D9A582" />
                        <ellipse cx="122" cy="88" rx="4" ry="7" fill="#D9A582" />

                        {/* Cara — óvalo redondo */}
                        <ellipse cx="90" cy="84" rx="32" ry="36" fill="#EBC0A0" />

                        {/* Cabello — una sola forma limpia con raya al costado */}
                        <path d="M 60 82
                                 C 58 60 70 44 90 44
                                 C 110 44 122 58 122 80
                                 C 122 76 120 72 117 70
                                 C 110 64 100 62 92 64
                                 C 88 58 80 56 72 60
                                 C 64 64 60 72 60 82 Z" fill="#1A2238" />
                        {/* Mechón frontal con caída diagonal */}
                        <path d="M 72 62
                                 C 80 56 96 56 108 62
                                 C 104 68 96 70 88 70
                                 C 82 72 76 70 72 66 Z" fill="#1A2238" />

                        {/* Mejillas — rubor suave */}
                        <ellipse cx="70" cy="96" rx="8" ry="4" fill="#E89B85" opacity="0.5" />
                        <ellipse cx="110" cy="96" rx="8" ry="4" fill="#E89B85" opacity="0.5" />

                        {/* Cejas */}
                        <path d="M 65 78 Q 73 75 80 78" stroke="#1A2238" strokeWidth="2.6" fill="none" strokeLinecap="round" />
                        <path d="M 100 78 Q 107 75 115 78" stroke="#1A2238" strokeWidth="2.6" fill="none" strokeLinecap="round" />

                        {/* Ojo izq */}
                        <g style={{
                            transform: `scaleY(${blink ? 0.1 : 1})`,
                            transformOrigin: '73px 86px',
                            transition: 'transform 0.08s ease-in-out'
                        }}>
                            <ellipse cx="73" cy="86" rx="2.8" ry="3.4" fill="#1A2238" />
                        </g>

                        {/* Ojo der */}
                        <g style={{
                            transform: `scaleY(${blink ? 0.1 : 1})`,
                            transformOrigin: '107px 86px',
                            transition: 'transform 0.08s ease-in-out'
                        }}>
                            <ellipse cx="107" cy="86" rx="2.8" ry="3.4" fill="#1A2238" />
                        </g>

                        {/* Nariz */}
                        <path d="M 88 90 Q 86 100 90 102 Q 94 102 92 100 L 90 90 Z" fill="#D9A582" opacity="0.55" />

                        {/* Boca */}
                        {isSpeaking ? (
                            mouthOpen ? (
                                <g>
                                    <path d="M 82 108 Q 90 116 98 108 Q 96 113 90 113.5 Q 84 113 82 108 Z" fill="#5C2A1F" />
                                    <rect x="84" y="108.5" width="12" height="2.5" rx="1" fill="#FFFFFF" />
                                </g>
                            ) : (
                                <path d="M 84 110 Q 90 113 96 110" stroke="#5C2A1F" strokeWidth="2" fill="none" strokeLinecap="round" />
                            )
                        ) : (
                            <path
                                d="M 84 110 Q 90 113 96 110"
                                stroke="#5C2A1F"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                            />
                        )}

                    </g>
                    </g>

                </g>
            </svg>

            {/* Indicador de estado — círculo flat con borde gris suave */}
            <div
                className="absolute rounded-full transition-all duration-300"
                style={{
                    width: 26, height: 26,
                    bottom: 2, right: 2,
                    zIndex: 2,
                    background: '#DCE3E5',
                    padding: 4,
                }}
            >
                <div
                    className="rounded-full w-full h-full"
                    style={{
                        background: isThinking ? '#F5B848' : '#5DD39E',
                    }}
                />
            </div>
        </div>
    );
};

export default AnimatedAvatar;
